import React, { useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { observeDigits } from '@/i18n/localeDigits';

/**
 * ShadowMount — инфраструктура изоляции React-острова от легаси-CSS
 * через Shadow DOM.
 *
 * Зачем
 * -----
 * Легаси-сайт грузит Bootstrap 3 + normalize.css, у которых element-level
 * правила вроде `a { color: #009dee }`, `input[type="file"] { display: block }`
 * со специфичностью (0,0,1)–(0,0,2). CSS-скоупинг не способен их перебить
 * без эскалации специфичности до уровня, который сам бьёт компонентные
 * стили — это математически тупик. Shadow DOM даёт физическую изоляцию:
 * стили легаси не входят внутрь, наши не выходят наружу.
 *
 * Как работает
 * ------------
 * 1. Лениво вешает shadow root (`{ mode: 'open' }`) на хост-элемент.
 * 2. Создаёт `<style>`-точку для styled-components через
 *    `<StyleSheetManager target={shadow}>` — все стили, инжектируемые
 *    children, попадают **в shadow**, а не в `<head>` документа.
 * 3. Создаёт собственный `@emotion/cache` с `container=shadow root`
 *    и оборачивает детей в `<CacheProvider>` — критично для библиотек,
 *    использующих emotion внутри (`react-select` v5 и т.п.). Без этого
 *    их стили инжектятся в `document.head` и физически не достают до
 *    элементов внутри shadow: у `react-select` ломается a11y-LiveRegion
 *    (текст «Select X. 1 of N» становится виден над полем),
 *    flex/grid контрола (слетает вертикальная центровка), `max-height`
 *    выпадающего меню и `DummyInput` для `isSearchable={false}`
 *    (ввод/фокус с клавиатуры перестаёт работать).
 * 4. Порталит children через `createPortal(..., shadowRoot)` — React
 *    рендерит детей внутрь shadow, но они остаются в той же React-tree
 *    (контекст ThemeProvider / Redux / i18n / router пропускается через
 *    портал штатно).
 *
 * shouldForwardProp нужен, чтобы styled-components не пробрасывал
 * styled-only пропы на DOM-элементы (как раньше в `App.tsx`/`AppIslands.tsx`).
 * Если хост-узел уже имеет shadowRoot (повторный mount React, HMR
 * и т.п.), переиспользуем существующий — повторный `attachShadow`
 * выбросит исключение.
 *
 * Использование
 * -------------
 * - `src/islands/IslandsContainer.tsx` — каждый остров;
 * - `src/components/organisms/Modal/Modal.tsx` — модалки на `#modal-root`.
 *
 * Внутри ShadowMount рендерим `<InsideShadowReset />` из
 * `src/styles/IslandScope.tsx` (там обычные element-селекторы без
 * `:where()` — внутри shadow нет легаси для битвы за специфичность)
 * и контент острова.
 */

function defaultShouldForwardProp(propName: string, target: unknown): boolean {
	if (typeof target === 'string') {
		return isPropValid(propName);
	}
	return true;
}

interface Props {
	/** DOM-узел, на который вешаем shadow root и в который порталим children. */
	host: Element;
	children: React.ReactNode;
}

/**
 * В jsdom (`NODE_ENV === 'test'`) shadow DOM работает, но Testing
 * Library не пробивает shadow-границу — `screen.getByText` ищет только
 * в light DOM. Все тесты на модалки/острова падали бы, потому что их
 * содержимое физически лежит в shadow. Чтобы тесты не пришлось
 * переписывать под shadow-aware queries, в тестовом окружении
 * ShadowMount деградирует до обычного `createPortal(host)` — light DOM,
 * как до Shadow DOM-рефактора. Это не влияет на dev/prod: там это
 * полноценный shadow + StyleSheetManager.
 */
const SKIP_SHADOW =
	typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

/**
 * Per-host opt-out из Shadow DOM через атрибут `data-no-shadow="1"`.
 * Применяется к Island'ам, которые:
 *   1) полагаются на легаси-CSS (Bootstrap, app.css) — `.container`,
 *      `.ts__form`, `.ts__section__*` — внутри shadow эти стили не
 *      достанут, и компонент сломается визуально;
 *   2) интегрируются с внешним SDK, который работает только в light DOM
 *      (Яндекс.Директ: `Ya.Context.AdvManager.render({renderTo: id})`
 *      делает `document.getElementById(id)` — а shadow для него невидим).
 *
 * Сейчас это TestResult и TestProcess: оба полно-страничные компоненты
 * со встроенной рекламой, для них изоляция от Bootstrap не нужна (страница
 * наша, не embed-виджет в чужом сайте).
 */
const hasNoShadowOptOut = (host: Element): boolean =>
	host instanceof HTMLElement && host.dataset.noShadow != null;

/**
 * Глобальный счётчик, чтобы у каждого emotion-кэша внутри shadow root
 * был уникальный `key`. Без этого emotion в DEV ругается варнингом, если
 * на странице создано несколько cache'ей с одинаковым ключом, а у нас
 * каждый ShadowMount (остров, модалка) — это отдельный cache.
 *
 * Emotion жёстко валидирует key регуляркой `^[a-z][a-z-]*` — никаких
 * цифр и подчёркиваний. Поэтому конвертим счётчик в base26-буквы
 * (`a`, `b`, …, `z`, `aa`, `ab`, …) — счётчик растёт монотонно, ключи
 * уникальны.
 */
let emotionCacheKeyCounter = 0;

const toBase26Letters = (n: number): string => {
	let result = '';
	let value = n;
	do {
		result = String.fromCharCode(97 + (value % 26)) + result;
		value = Math.floor(value / 26) - 1;
	} while (value >= 0);
	return result;
};

const ShadowMount: React.FC<Props> = ({ host, children }) => {
	const skipShadow = SKIP_SHADOW || hasNoShadowOptOut(host);
	const [shadow, setShadow] = useState<ShadowRoot | null>(null);

	useLayoutEffect(() => {
		if (skipShadow) return;
		if (!(host instanceof HTMLElement)) return;
		const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
		setShadow(root);
		// Национальные цифры локали (bn: ০১২৩) внутри shadow — наблюдатель за
		// текстовыми узлами острова; для языков с западными цифрами — no-op.
		// Light DOM (в т.ч. острова с data-no-shadow) наблюдается из main.islands.tsx.
		return observeDigits(root);
	}, [host, skipShadow]);

	/**
	 * Emotion-кэш с `container = shadow root`. `prepend: true` ставит
	 * наш `<style>`-тег первым в shadow, чтобы element-level reset из
	 * `InsideShadowReset` (styled-components) шёл ниже и мог переопределять
	 * дефолты библиотечных классов (как раньше в `<head>`).
	 *
	 * `key` обязан быть уникальным per-instance: emotion использует его
	 * как префикс class-name'ов и сравнивает со своим списком cache'ей.
	 */
	const cache = useMemo(() => {
		if (!shadow) return null;
		const key = `shadow-${toBase26Letters(emotionCacheKeyCounter)}`;
		emotionCacheKeyCounter += 1;
		return createCache({
			key,
			container: shadow as unknown as Node,
			prepend: true,
		});
	}, [shadow]);

	/**
	 * Помечаем host `data-island-ready`, когда контент реально смонтирован:
	 * light-DOM острова (skipShadow) — сразу (createPortal рендерит в том же
	 * коммите); shadow-острова — когда shadow+cache готовы и портал отрисован.
	 * По этому атрибуту islands.css прячет скелетон-заглушку
	 * (`.react-island__skeleton`). У shadow-островов заглушка и так исчезает
	 * при attachShadow (light-дети host'а перестают рендериться), атрибут —
	 * страховка и единственный способ спрятать её у light-островов.
	 */
	useLayoutEffect(() => {
		if (!(host instanceof HTMLElement)) return;
		const contentMounted = skipShadow || (!!shadow && !!cache);
		if (contentMounted) {
			host.setAttribute('data-island-ready', '');
		}
	}, [host, skipShadow, shadow, cache]);

	if (skipShadow) {
		return createPortal(children, host);
	}

	if (!shadow || !cache) return null;

	return createPortal(
		<StyleSheetManager
			target={shadow as unknown as HTMLElement}
			shouldForwardProp={defaultShouldForwardProp}
		>
			<CacheProvider value={cache}>{children}</CacheProvider>
		</StyleSheetManager>,
		shadow as unknown as Element,
	);
};

export default ShadowMount;

/**
 * `document.activeElement` возвращает shadow host (внешний DOM-узел),
 * а не реально сфокусированный элемент внутри shadow. Для focus-trap
 * и «вернуть фокус откуда был» (см. `Modal.tsx`) нужен deep-обход
 * вложенных shadow-границ.
 *
 * Если в проекте появятся вложенные острова (shadow внутри shadow),
 * цикл while сам прокопает все слои.
 */
export const getDeepActiveElement = (): Element | null => {
	if (typeof document === 'undefined') return null;
	let el: Element | null = document.activeElement;
	while (el instanceof HTMLElement && el.shadowRoot?.activeElement) {
		el = el.shadowRoot.activeElement;
	}
	return el;
};
